import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store';
import { localApi, type QuestionChangeRequest } from '../utils/local-api';
import { Trash2, Edit, Plus, Upload, Download } from 'lucide-react';
import { isAdminRole } from '../utils/permissions';

type QuestionEditorState = {
  id: string;
  level: number;
  part: string;
  question_text: string;
  options: string[];
  correct_index: number;
  explanation?: string | null;
  question_group?: string | null;
  group_seq?: number | null;
};

type QuestionBankVersion = '2.0' | '3.0';

const isW1Part = (part?: string) => String(part || '').trim().toUpperCase() === 'W1';

const splitQuestionTokens = (text: string) =>
  String(text || '')
    .split(';')
    .map((token) => token.trim())
    .filter(Boolean);

const deriveW1OptionsFromAnswer = (questionText: string, answerText: string): string[] | null => {
  const tokens = splitQuestionTokens(questionText);
  const rawAnswer = String(answerText || '').trim();
  if (!tokens.length || !rawAnswer) return null;

  if (rawAnswer.includes(';')) {
    const explicit = rawAnswer.split(';').map((token) => token.trim()).filter(Boolean);
    if (explicit.length !== tokens.length) return null;
    if (tokens.some((token) => !explicit.includes(token))) return null;
    return explicit;
  }

  const ranked = tokens.map((token, idx) => ({
    token,
    idx,
    pos: rawAnswer.indexOf(token)
  }));
  if (ranked.some((item) => item.pos < 0)) return null;
  ranked.sort((a, b) => a.pos - b.pos || a.idx - b.idx);
  return ranked.map((item) => item.token);
};

const stripJsonBom = (value: string) => String(value || '').replace(/^\uFEFF/, '');

const extractQuestionArray = (payload: unknown): any[] | null => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return null;

  const record = payload as Record<string, unknown>;
  if (Array.isArray(record.items)) return record.items as any[];
  if (Array.isArray(record.questions)) return record.questions as any[];
  if (Array.isArray(record.data)) return record.data as any[];

  // Support grouped exports like { "H41001": [...], "H41002": [...] } by
  // flattening all top-level array values into one import list.
  const topLevelArrays = Object.values(record).filter(Array.isArray) as any[][];
  if (topLevelArrays.length > 0) {
    return topLevelArrays.flat();
  }

  return null;
};

const normalizeBulkQuestionItem = (
  item: any,
  useDefaults: boolean,
  defaultLevel: number,
  defaultPart: string
) => {
  const questionId = String(item?.id || item?.question_id || item?.questionId || '').trim();
  const levelValue = item?.level ?? (useDefaults ? defaultLevel : null);
  const level = levelValue == null || levelValue === '' ? null : Number(levelValue);
  const part = String(item?.part || item?.questionPart || (useDefaults ? (defaultPart || 'L1') : '')).trim();
  const questionText = String(item?.question || item?.question_text || item?.questionText || '').trim();
  const media = item?.media || item?.audio || item?.image || null;
  const options = Array.isArray(item?.options)
    ? item.options
    : (typeof item?.options_semicolon === 'string'
      ? item.options_semicolon.split(';').map((option: string) => option.trim()).filter(Boolean)
      : []);
  const correctIndexRaw = item?.correctAnswer ?? item?.correct_index ?? item?.correctIndex ?? 0;
  const vocabIds = Array.isArray(item?.vocabIds) ? item.vocabIds : (Array.isArray(item?.vocab_ids) ? item.vocab_ids : []);
  const explanation = item?.explanation || item?.analysis || null;
  const questionGroup = item?.question_group || item?.group_id || item?.groupId || null;
  const rawGroupSeq = item?.group_seq ?? item?.seq ?? item?.groupSeq ?? null;
  const groupSeq = rawGroupSeq == null || rawGroupSeq === '' ? null : Number(rawGroupSeq);

  if (!questionId || level == null || Number.isNaN(level) || !part) {
    return null;
  }

  return {
    question_id: questionId,
    level,
    part,
    question_text: questionText,
    media,
    options,
    correct_index: Number(correctIndexRaw) || 0,
    vocab_ids: vocabIds,
    explanation,
    question_group: questionGroup,
    group_seq: Number.isNaN(groupSeq as number) ? null : groupSeq
  };
};

const AdminQuestions: React.FC = () => {
  const { token, user } = useAuthStore();
  const isAdmin = isAdminRole(user?.role);
  const [bankVersion, setBankVersion] = useState<QuestionBankVersion>('2.0');
  const [level, setLevel] = useState<number | null>(3);
  const [parts, setParts] = useState<Array<{ part: string; cnt: number }>>([]);
  const [part, setPart] = useState<string>('');
  const [items, setItems] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [limit, setLimit] = useState(20);
  const [offset, setOffset] = useState(0);
  const [editing, setEditing] = useState<QuestionEditorState | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState('');
  const [creating, setCreating] = useState(false);
  const [bulkCreating, setBulkCreating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [uploadTarget, setUploadTarget] = useState<string|null>(null);
  const [myRequests, setMyRequests] = useState<QuestionChangeRequest[]>([]);
  const [adminRequests, setAdminRequests] = useState<QuestionChangeRequest[]>([]);
  const [requestError, setRequestError] = useState('');
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [reviewingRequestId, setReviewingRequestId] = useState<string | null>(null);
  const [refreshingRequests, setRefreshingRequests] = useState(false);
  const [rejectingRequestId, setRejectingRequestId] = useState<string | null>(null);
  const [rejectComment, setRejectComment] = useState('');

  const partSummary = useMemo(() => {
    const groups: Record<string, number[]> = {};
    parts.forEach(p => {
      const m = p.part.match(/^([LRW])(\d+)$/i);
      if (m) {
        const key = m[1].toUpperCase();
        const n = parseInt(m[2], 10);
        groups[key] = groups[key] ? [...groups[key], n] : [n];
      }
    });
    const keys = Object.keys(groups).sort();
    return keys.map(k => `${k}${Math.min(...groups[k])}-${k}${Math.max(...groups[k])}`).join('，');
  }, [parts]);

  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const rangeStart = total === 0 ? 0 : offset + 1;
  const rangeEnd = total === 0 ? 0 : Math.min(offset + items.length, total);

  const pageItems = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const pages: Array<number | 'ellipsis'> = [1];
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    if (start > 2) pages.push('ellipsis');
    for (let page = start; page <= end; page += 1) {
      pages.push(page);
    }
    if (end < totalPages - 1) pages.push('ellipsis');
    pages.push(totalPages);

    return pages;
  }, [currentPage, totalPages]);

  const requestStatusTone: Record<QuestionChangeRequest['status'], string> = {
    pending: 'bg-amber-100 text-amber-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    cancelled: 'bg-gray-100 text-gray-700'
  };

  const loadRequests = async () => {
    if (!token) return;
    try {
      setRefreshingRequests(true);
      setRequestError('');
      const [mineRes, adminRes] = await Promise.all([
        localApi.getMyQuestionChangeRequests(token),
        isAdmin ? localApi.getAdminQuestionChangeRequests(token, 'pending') : Promise.resolve({ success: true, requests: [] as QuestionChangeRequest[] })
      ]);
      if (mineRes.success) setMyRequests(mineRes.requests);
      if (adminRes.success) setAdminRequests(adminRes.requests);
    } catch (e: any) {
      setRequestError(e?.message || '加载审批记录失败');
    } finally {
      setRefreshingRequests(false);
    }
  };

  const buildQuestionPayload = (source: Partial<QuestionEditorState> & Record<string, any>, optionsOverride?: string[]) => ({
    question_id: String(source.id || source.question_id || '').trim(),
    level: Number(source.level || 0),
    part: String(source.part || ''),
    question_text: String(source.question_text || ''),
    media: source.media ?? null,
    options: optionsOverride ?? (Array.isArray(source.options) ? source.options : []),
    correct_index: Number(source.correct_index || 0),
    vocab_ids: Array.isArray(source.vocab_ids) ? source.vocab_ids : [],
    explanation: source.explanation ?? null,
    question_group: source.question_group ?? null,
    group_seq: source.group_seq == null || source.group_seq === '' ? null : Number(source.group_seq)
  });

  const submitChangeRequest = async (payload: { question_id?: string; change_type: 'create' | 'update' | 'delete'; proposed_snapshot?: any }) => {
    if (!token) return;
    setSubmittingRequest(true);
    setRequestError('');
    try {
      await localApi.createQuestionChangeRequest({ ...payload, bankVersion }, token);
      await loadRequests();
    } finally {
      setSubmittingRequest(false);
    }
  };

  const loadParts = async () => {
    if (!token) return;
    try {
      setLoading(true);
      const res = await localApi.listParts(level ?? undefined, token, bankVersion);
      setParts(res.parts);
      if (!part && res.parts.length > 0) setPart(res.parts[0].part);
    } catch (e: any) {
      setError(e.message || 'Failed to load parts');
    } finally {
      setLoading(false);
    }
  };

  const loadItems = async () => {
    if (!token) return;
    try {
      setLoading(true);
      const res = await localApi.browseQuestions({ level: level ?? undefined, part: part || undefined, limit, offset, bankVersion }, token);
      setItems(res.questions);
      setTotal(Number(res.total || 0));
    } catch (e: any) {
      setError(e.message || 'Failed to load questions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadParts(); }, [level, bankVersion]);
  useEffect(() => { loadItems(); }, [level, part, limit, offset, bankVersion]);
  useEffect(() => { loadRequests(); }, [token, isAdmin, bankVersion]);
  useEffect(() => { setOffset(0); }, [level, part, limit, bankVersion]);

  useEffect(() => {
    setPart('');
  }, [bankVersion]);

  useEffect(() => {
    if (offset > 0 && total > 0 && offset >= total) {
      setOffset(Math.max(0, (Math.ceil(total / limit) - 1) * limit));
    }
  }, [offset, total, limit]);

  const goToPage = (page: number) => {
    const safePage = Math.min(Math.max(1, page), totalPages);
    setOffset((safePage - 1) * limit);
  };

  const onDelete = async (id: string) => {
    if (isAdmin) {
      await localApi.deleteQuestion(id, token, bankVersion);
    } else {
      await submitChangeRequest({ question_id: id, change_type: 'delete' });
      alert('删除申请已提交给 admin 审批');
    }
    await loadItems();
  };

  const onSaveEdit = async () => {
    if (!editing) return;
    const cleanedOptions = editing.options.map(o => String(o ?? '').trim());
    const hasAnyOption = cleanedOptions.some(Boolean);

    if (isW1Part(editing.part)) {
      const derivedOptions = deriveW1OptionsFromAnswer(editing.question_text, editing.explanation || '');
      if (!derivedOptions) {
        setEditError('W1 的答案句子无法和题干词块匹配。请确保题干用 ; 分隔，或答案里也用 ; 按顺序填写。');
        return;
      }

      try {
        setSavingEdit(true);
        setEditError('');
        const payload = {
          level: editing.level,
          part: editing.part,
          question_text: editing.question_text,
          options: derivedOptions,
          correct_index: 0,
          explanation: editing.explanation ?? null,
          question_group: editing.question_group ?? null,
          group_seq: editing.group_seq ?? null
        };
        if (isAdmin) {
          await localApi.updateQuestion(editing.id, payload, token, bankVersion);
        } else {
          await submitChangeRequest({
            question_id: editing.id,
            change_type: 'update',
            proposed_snapshot: buildQuestionPayload({ ...editing, options: derivedOptions, correct_index: 0 })
          });
          alert('题目修改已提交给 admin 审批');
        }
        setEditing(null);
        await loadItems();
      } catch (e: any) {
        setEditError(e?.message || '保存失败');
      } finally {
        setSavingEdit(false);
      }
      return;
    }

    if (hasAnyOption && (editing.correct_index < 0 || editing.correct_index >= cleanedOptions.length)) {
      setEditError('正确答案索引超出选项范围');
      return;
    }

    if (hasAnyOption && !cleanedOptions[editing.correct_index]) {
      setEditError('正确答案对应的选项不能为空');
      return;
    }

    try {
      setSavingEdit(true);
      setEditError('');
      const payload = {
        level: editing.level,
        part: editing.part,
        question_text: editing.question_text,
        options: cleanedOptions,
        correct_index: editing.correct_index,
        explanation: editing.explanation ?? null,
        question_group: editing.question_group ?? null,
        group_seq: editing.group_seq ?? null
      };
      if (isAdmin) {
        await localApi.updateQuestion(editing.id, payload, token, bankVersion);
      } else {
        await submitChangeRequest({
          question_id: editing.id,
          change_type: 'update',
          proposed_snapshot: buildQuestionPayload({ ...editing, options: cleanedOptions })
        });
        alert('题目修改已提交给 admin 审批');
      }
      setEditing(null);
      await loadItems();
    } catch (e: any) {
      setEditError(e?.message || '保存失败');
    } finally {
      setSavingEdit(false);
    }
  };

  const onCreate = async (payload: any) => {
    if (isAdmin) {
      await localApi.createQuestion(payload, token, bankVersion);
    } else {
      await submitChangeRequest({
        question_id: payload.question_id,
        change_type: 'create',
        proposed_snapshot: buildQuestionPayload({ ...payload, id: payload.question_id })
      });
      alert('新增题目已提交给 admin 审批');
    }
    setCreating(false);
    await loadItems();
  };

  const reviewRequest = async (requestId: string, action: 'approve' | 'reject') => {
    if (!token) return;
    try {
      setReviewingRequestId(requestId);
      if (action === 'approve') {
        await localApi.approveQuestionChangeRequest(requestId, token);
      } else {
        await localApi.rejectQuestionChangeRequest(requestId, rejectComment.trim(), token);
        setRejectingRequestId(null);
        setRejectComment('');
      }
      await Promise.all([loadItems(), loadRequests()]);
    } catch (e: any) {
      alert(e?.message || '审批失败');
    } finally {
      setReviewingRequestId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">题库管理</h1>
              <p className="text-gray-600">
                {isAdmin ? '直接维护正式题库。' : '老师可编辑题目，但提交后需等待 admin 审批。'}
                {partSummary && ` 类型范围：${partSummary}`}
              </p>
            </div>
            <div className="flex items-center space-x-2">
            <button onClick={() => setCreating(true)} className="inline-flex items-center px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700">
              <Plus className="w-4 h-4 mr-2" />新增题目
            </button>
            {isAdmin && (
            <>
            <Link to="/admin/question-approvals" className="inline-flex items-center px-3 py-2 bg-slate-700 text-white rounded hover:bg-slate-800">
              查看审批
            </Link>
            <button onClick={() => setBulkCreating(true)} className="inline-flex items-center px-3 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700">
              <Upload className="w-4 h-4 mr-2" />批量新增
            </button>
            <button onClick={() => setExporting(true)} className="inline-flex items-center px-3 py-2 bg-orange-600 text-white rounded hover:bg-orange-700">
              <Download className="w-4 h-4 mr-2" />批量导出
            </button>
            </>
            )}
            </div>
          </div>
        </div>
      </div>

      {requestError && (
        <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {requestError}
        </div>
      )}

      {!isAdmin && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">我的提审记录</h2>
              <button onClick={loadRequests} className="px-3 py-2 border rounded text-sm hover:bg-gray-50">
                {refreshingRequests ? '刷新中...' : '刷新'}
              </button>
            </div>
            {myRequests.length === 0 ? (
              <div className="text-sm text-gray-500">还没有提交过题目修改。</div>
            ) : (
              <div className="space-y-3">
                {myRequests.slice(0, 8).map((request) => (
                  <div key={request.id} className="rounded border border-gray-200 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{request.question_id} · {request.bank_version}</div>
                        <div className="text-xs text-gray-500">{request.change_type} · {new Date(request.submitted_at).toLocaleString()}</div>
                      </div>
                      <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${requestStatusTone[request.status]}`}>
                        {request.status}
                      </span>
                    </div>
                    {request.review_comment && <div className="mt-2 text-sm text-red-600">备注：{request.review_comment}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {isAdmin && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">待审批题目修改</h2>
              <button onClick={loadRequests} className="px-3 py-2 border rounded text-sm hover:bg-gray-50">
                {refreshingRequests ? '刷新中...' : '刷新'}
              </button>
            </div>
            {adminRequests.length === 0 ? (
              <div className="text-sm text-gray-500">当前没有待审批修改。</div>
            ) : (
              <div className="space-y-4">
                {adminRequests.map((request) => (
                  <div key={request.id} className="rounded border border-amber-200 bg-amber-50/40 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-gray-900">{request.question_id} · {request.bank_version}</div>
                        <div className="text-xs text-gray-500">
                          {request.change_type} · 提交人 {request.submitted_by} · {new Date(request.submitted_at).toLocaleString()}
                        </div>
                      </div>
                      <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${requestStatusTone[request.status]}`}>
                        {request.status}
                      </span>
                    </div>
                    <div className="mt-3 grid gap-3 md:grid-cols-2">
                      <div className="rounded border border-gray-200 bg-white p-3">
                        <div className="mb-1 text-xs font-medium uppercase text-gray-500">原始内容</div>
                        {request.original_snapshot ? (
                          <>
                            <div className="text-sm text-gray-900">{request.original_snapshot.question_text || '(无题干)'}</div>
                            <div className="mt-2 text-xs text-gray-500">Part: {request.original_snapshot.part} · Level: {request.original_snapshot.level}</div>
                          </>
                        ) : (
                          <div className="text-sm text-gray-400">新建题目，无原始内容</div>
                        )}
                      </div>
                      <div className="rounded border border-gray-200 bg-white p-3">
                        <div className="mb-1 text-xs font-medium uppercase text-gray-500">提交内容</div>
                        {request.proposed_snapshot ? (
                          <>
                            <div className="text-sm text-gray-900">{request.proposed_snapshot.question_text || '(无题干)'}</div>
                            <div className="mt-2 text-xs text-gray-500">Part: {request.proposed_snapshot.part} · Level: {request.proposed_snapshot.level}</div>
                          </>
                        ) : (
                          <div className="text-sm text-gray-400">删除申请，无新内容</div>
                        )}
                      </div>
                    </div>
                    <div className="mt-3 flex gap-2 justify-end">
                      <button
                        onClick={() => {
                          setRejectingRequestId(request.id);
                          setRejectComment('');
                        }}
                        disabled={reviewingRequestId === request.id}
                        className="px-3 py-2 rounded border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50"
                      >
                        拒绝
                      </button>
                      <button
                        onClick={() => reviewRequest(request.id, 'approve')}
                        disabled={reviewingRequestId === request.id}
                        className="px-3 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                      >
                        {reviewingRequestId === request.id ? '处理中...' : '批准'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6 grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">题库版本</label>
            <select value={bankVersion} onChange={e => setBankVersion(e.target.value as QuestionBankVersion)} className="mt-1 block w-full border rounded px-3 py-2">
              <option value="2.0">HSK2.0</option>
              <option value="3.0">HSK3.0</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">HSK 等级</label>
            <select value={level ?? ''} onChange={e => setLevel(e.target.value ? Number(e.target.value) : null)} className="mt-1 block w-full border rounded px-3 py-2">
              {[1,2,3,4,5,6].map(l => (<option key={l} value={l}>{l}</option>))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">题目类型</label>
            <select value={part} onChange={e => setPart(e.target.value)} className="mt-1 block w-full border rounded px-3 py-2">
              <option value="">全部</option>
              {parts.map(p => (<option key={p.part} value={p.part}>{p.part}（{p.cnt}）</option>))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">每页数量</label>
            <select value={limit} onChange={e => setLimit(Number(e.target.value))} className="mt-1 block w-full border rounded px-3 py-2">
              {[10,20,50,100].map(n => (<option key={n} value={n}>{n}</option>))}
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {loading ? (
          <div className="p-6 text-gray-600">加载中...</div>
        ) : error ? (
          <div className="p-6 text-red-600">{error}</div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {items.map((q: any) => (
              <li key={q.question_id} className="px-4 py-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="text-sm text-gray-500">{q.level} · {q.part} · {q.question_id}</div>
                    <div className="text-gray-900 font-medium mb-2">
                      {q.question_text ? (
                        q.question_text.includes(';') ? (
                          q.question_text.split(';').map((t: string, i: number) => <div key={i}>{t}</div>)
                        ) : q.question_text
                      ) : '(无题干)'}
                    </div>
                    {isW1Part(q.part) ? (
                      <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                        答案：{q.explanation || (Array.isArray(q.options) ? q.options.filter(Boolean).join('') : '') || '未设置'}
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        {q.options?.map((o: string, i: number) => (
                          <div key={i} className={`px-3 py-2 border rounded ${q.correct_index===i?'border-green-500 bg-green-50':'border-gray-200'}`}>{String.fromCharCode(65+i)}. {o}</div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="ml-4 flex items-center space-x-2">
                    <div className="text-xs text-gray-500">
                      {q.media?.image && <a href={q.media.image} target="_blank" rel="noreferrer" className="text-blue-600 mr-2">Image</a>}
                      {q.media?.audio && <a href={q.media.audio} target="_blank" rel="noreferrer" className="text-green-600">Audio</a>}
                    </div>
                    <button onClick={() => {
                      const nextOptions = Array.isArray(q.options) && q.options.length > 0 ? q.options : ['', '', '', ''];
                      setEditError('');
                      setEditing({
                        id: q.question_id,
                        level: Number(q.level || level || 0),
                        part: q.part || '',
                        question_text: q.question_text || '',
                        options: nextOptions,
                        correct_index: Number(q.correct_index || 0),
                        explanation: q.explanation || '',
                        question_group: q.question_group ?? null,
                        group_seq: q.group_seq ?? null
                      });
                    }} className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"><Edit className="w-4 h-4" /></button>
                    {isAdmin && <button onClick={() => setUploadTarget(q.question_id)} className="px-3 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"><Upload className="w-4 h-4" /></button>}
                    <button onClick={() => onDelete(q.question_id)} disabled={submittingRequest} className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-4 sm:px-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="text-sm text-gray-600">
            {total > 0 ? `显示 ${rangeStart}-${rangeEnd} / ${total} 条` : '暂无数据'}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => goToPage(1)}
              disabled={currentPage === 1 || total === 0}
              className="rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              First
            </button>
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1 || total === 0}
              className="rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Previous
            </button>

            {pageItems.map((pageItem, index) => (
              pageItem === 'ellipsis' ? (
                <span key={`ellipsis-${index}`} className="px-2 text-sm text-gray-400">...</span>
              ) : (
                <button
                  key={pageItem}
                  onClick={() => goToPage(pageItem)}
                  className={`min-w-[40px] rounded px-3 py-2 text-sm font-medium transition ${
                    pageItem === currentPage
                      ? 'bg-orange-500 text-white'
                      : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {pageItem}
                </button>
              )
            ))}

            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages || total === 0}
              className="rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
            </button>
            <button
              onClick={() => goToPage(totalPages)}
              disabled={currentPage === totalPages || total === 0}
              className="rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Last
            </button>
          </div>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center">
          <div className="bg-white rounded shadow p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">编辑题目 {editing.id}</h3>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">等级</label>
                <input type="number" value={editing.level} onChange={e => setEditing({ ...editing, level: Number(e.target.value) })} className="w-full border rounded px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">类型</label>
                <input value={editing.part} onChange={e => setEditing({ ...editing, part: e.target.value })} className="w-full border rounded px-3 py-2" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">题组 ID</label>
                <input value={editing.question_group || ''} onChange={e => setEditing({ ...editing, question_group: e.target.value || null })} className="w-full border rounded px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">题组顺序</label>
                <input type="number" value={editing.group_seq ?? ''} onChange={e => setEditing({ ...editing, group_seq: e.target.value ? Number(e.target.value) : null })} className="w-full border rounded px-3 py-2" />
              </div>
            </div>
            <label className="block text-sm font-medium text-gray-700 mb-1">题干</label>
            <textarea value={editing.question_text} onChange={e => setEditing({ ...editing, question_text: e.target.value })} className="w-full border rounded px-3 py-2 mb-3" rows={3} />
            {isW1Part(editing.part) ? (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">答案句子</label>
                <textarea
                  value={editing.explanation || ''}
                  onChange={e => setEditing({ ...editing, explanation: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  rows={3}
                  placeholder="例如：上午的考试比较简单"
                />
                <p className="mt-1 text-xs text-gray-500">W1 不需要手动编辑选项。系统会根据题干中用 ; 分隔的词块，自动生成正确顺序。</p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">选项内容</label>
                  <button
                    type="button"
                    onClick={() => setEditing({ ...editing, options: [...editing.options, ''] })}
                    className="px-2 py-1 text-xs border rounded hover:bg-gray-50"
                  >
                    新增选项
                  </button>
                </div>
                <div className="space-y-2 mb-4">
                  {editing.options.map((o, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <label className="flex items-center gap-2 text-sm text-gray-700 min-w-[64px]">
                        <input
                          type="radio"
                          name="correct-option"
                          checked={editing.correct_index === i}
                          onChange={() => setEditing({ ...editing, correct_index: i })}
                        />
                        {String.fromCharCode(65 + i)}
                      </label>
                      <input
                        value={o}
                        onChange={e => {
                          const next = [...editing.options];
                          next[i] = e.target.value;
                          setEditing({ ...editing, options: next });
                        }}
                        className="flex-1 border rounded px-3 py-2"
                        placeholder={`选项 ${String.fromCharCode(65 + i)}`}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (editing.options.length <= 1) return;
                          const next = editing.options.filter((_, idx) => idx !== i);
                          const nextCorrect = editing.correct_index > i
                            ? editing.correct_index - 1
                            : Math.min(editing.correct_index, next.length - 1);
                          setEditing({ ...editing, options: next, correct_index: nextCorrect });
                        }}
                        className="px-2 py-2 text-xs border rounded hover:bg-red-50"
                      >
                        删除
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
            {editError && <div className="text-sm text-red-600 mb-3">{editError}</div>}
            <div className="flex justify-end space-x-2">
              <button onClick={() => { setEditing(null); setEditError(''); }} className="px-3 py-2 border rounded">取消</button>
              <button onClick={onSaveEdit} disabled={savingEdit || submittingRequest} className="px-3 py-2 bg-blue-600 text-white rounded disabled:opacity-50">
                {savingEdit || submittingRequest ? (isAdmin ? '保存中...' : '提交中...') : (isAdmin ? '保存' : '提交审核')}
              </button>
            </div>
          </div>
        </div>
      )}

      {creating && (
        <CreateDialog onCancel={() => setCreating(false)} onCreate={onCreate} defaultLevel={level ?? 3} defaultPart={part || ''} bankVersion={bankVersion} isAdmin={isAdmin} />
      )}

      {bulkCreating && (
        <BulkCreateDialog
          onCancel={() => setBulkCreating(false)}
          onCreate={async (items, onProgress) => {
            const result = await localApi.createQuestionsBulk(items, token, bankVersion, { onProgress });
            await loadItems();
            return result;
          }}
          defaultLevel={level ?? 3}
          defaultPart={part || ''}
          bankVersion={bankVersion}
          onBankVersionChange={setBankVersion}
        />
      )}
      {isAdmin && uploadTarget && (
        <UploadMediaDialog questionId={uploadTarget} onCancel={() => setUploadTarget(null)} onUploaded={async (media) => { await localApi.updateQuestion(uploadTarget, { media }, token, bankVersion); setUploadTarget(null); await loadItems(); }} />
      )}
      {isAdmin && exporting && (
        <ExportDialog onCancel={() => setExporting(false)} bankVersion={bankVersion} token={token} />
      )}
      {rejectingRequestId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h2 className="text-lg font-semibold text-gray-900">拒绝申请</h2>
            <p className="mt-2 text-sm text-gray-600">可选填写拒绝原因，保存后会显示给老师。</p>
            <textarea
              value={rejectComment}
              onChange={(e) => setRejectComment(e.target.value)}
              rows={4}
              placeholder="例如：需要补充依据，或正式题库已更新"
              className="mt-4 w-full rounded-xl border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => {
                  if (reviewingRequestId) return;
                  setRejectingRequestId(null);
                  setRejectComment('');
                }}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                取消
              </button>
              <button
                onClick={() => reviewRequest(rejectingRequestId, 'reject')}
                disabled={reviewingRequestId === rejectingRequestId}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                {reviewingRequestId === rejectingRequestId ? '提交中...' : '确认拒绝'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CreateDialog: React.FC<{ onCancel: ()=>void; onCreate: (payload:any)=>void; defaultLevel: number; defaultPart: string; bankVersion: QuestionBankVersion; isAdmin: boolean }> = ({ onCancel, onCreate, defaultLevel, defaultPart, bankVersion, isAdmin }) => {
  const [form, setForm] = useState<any>({ question_id: '', level: defaultLevel, part: defaultPart || 'L1', question_text: '', media: null, options: ['','','',''], correct_index: 0, vocab_ids: [], explanation: '', question_group: '', group_seq: '' });
  const [createError, setCreateError] = useState('');

  const submitCreate = () => {
    if (isW1Part(form.part)) {
      const derivedOptions = deriveW1OptionsFromAnswer(form.question_text, form.explanation || '');
      if (!derivedOptions) {
        setCreateError('W1 的答案句子无法和题干词块匹配。请确保题干用 ; 分隔，或答案里也用 ; 按顺序填写。');
        return;
      }
      setCreateError('');
      onCreate({ ...form, options: derivedOptions, correct_index: 0 });
      return;
    }

    setCreateError('');
    onCreate(form);
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center">
      <div className="bg-white rounded shadow p-6 w-full max-w-lg">
        <h3 className="text-lg font-semibold mb-4">新增题目</h3>
        <div className="mb-3 text-sm text-gray-600">
          {isAdmin ? '当前会直接写入正式题库：' : '当前会提交给 admin 审批：'}
          {bankVersion === '3.0' ? 'HSK3.0 / HSK3_question_bank' : 'HSK2.0 / HSK_question_bank'}
        </div>
        <label className="block text-sm font-medium text-gray-700 mb-1">题目ID</label>
        <input value={form.question_id} onChange={e => setForm({ ...form, question_id: e.target.value })} className="w-full border rounded px-3 py-2 mb-2" />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">等级</label>
            <input type="number" value={form.level} onChange={e => setForm({ ...form, level: Number(e.target.value) })} className="w-full border rounded px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">类型</label>
            <input value={form.part} onChange={e => setForm({ ...form, part: e.target.value })} className="w-full border rounded px-3 py-2" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">题组 ID</label>
            <input value={form.question_group} onChange={e => setForm({ ...form, question_group: e.target.value })} className="w-full border rounded px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">题组顺序</label>
            <input type="number" value={form.group_seq} onChange={e => setForm({ ...form, group_seq: e.target.value })} className="w-full border rounded px-3 py-2" />
          </div>
        </div>
        <label className="block text-sm font-medium text-gray-700 mb-1 mt-3">题干</label>
        <textarea value={form.question_text} onChange={e => setForm({ ...form, question_text: e.target.value })} className="w-full border rounded px-3 py-2 mb-2" rows={3} />
        {isW1Part(form.part) ? (
          <>
            <label className="block text-sm font-medium text-gray-700 mb-1">答案句子</label>
            <textarea
              value={form.explanation || ''}
              onChange={e => setForm({ ...form, explanation: e.target.value })}
              className="w-full border rounded px-3 py-2 mb-2"
              rows={3}
              placeholder="例如：上午的考试比较简单"
            />
            <p className="text-xs text-gray-500 mb-4">系统会根据题干中的 `;` 词块，自动生成正确顺序，不需要手动填写选项。</p>
          </>
        ) : (
          <>
            <label className="block text-sm font-medium text-gray-700 mb-1">选项</label>
            {form.options.map((o: string, i: number) => (
              <input key={i} value={o} onChange={e => { const next = [...form.options]; next[i] = e.target.value; setForm({ ...form, options: next }); }} className="w-full border rounded px-3 py-2 mb-2" />
            ))}
            <label className="block text-sm font-medium text-gray-700 mb-1">正确选项索引</label>
            <input type="number" value={form.correct_index} onChange={e => setForm({ ...form, correct_index: Number(e.target.value) })} className="w-full border rounded px-3 py-2 mb-4" />
          </>
        )}
        {createError && <div className="text-sm text-red-600 mb-3">{createError}</div>}
        <div className="flex justify-end space-x-2">
          <button onClick={onCancel} className="px-3 py-2 border rounded">取消</button>
          <button onClick={submitCreate} className="px-3 py-2 bg-green-600 text-white rounded">{isAdmin ? '创建' : '提交审核'}</button>
        </div>
      </div>
    </div>
  );
};

export default AdminQuestions;

const BulkCreateDialog: React.FC<{ onCancel: ()=>void; onCreate: (items:any[], onProgress?: (completed: number, total: number) => void) => Promise<{ success: boolean; inserted: number }>; defaultLevel: number; defaultPart: string; bankVersion: QuestionBankVersion; onBankVersionChange: (version: QuestionBankVersion) => void }> = ({ onCancel, onCreate, defaultLevel, defaultPart, bankVersion, onBankVersionChange }) => {
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const [useDefaults, setUseDefaults] = useState(true);
  const [fileName, setFileName] = useState('');
  const [fileText, setFileText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState('');
  const getSourceText = () => stripJsonBom((fileName ? fileText : text).trim());
  const parseItems = (): { items: any[]; error?: string } => {
    const sourceText = getSourceText();
    if (!sourceText) {
      return { items: [], error: '请先粘贴 JSON，或选择一个 JSON 文件' };
    }

    try {
      const parsed = JSON.parse(sourceText);
      const rawItems = extractQuestionArray(parsed);
      if (!rawItems) {
        return { items: [], error: 'JSON 根节点必须是数组，或包含 items / questions / data 数组字段' };
      }

      const items = rawItems
        .map((it: any) => normalizeBulkQuestionItem(it, useDefaults, defaultLevel, defaultPart))
        .filter(Boolean);

      if (rawItems.length > 0 && items.length === 0) {
        return {
          items: [],
          error: '已读取到 JSON 数组，但没有识别出可导入题目。请检查 question_id、level、part 字段，或勾选“使用当前等级与类型作为默认值”。'
        };
      }

      return { items };
    } catch {
      return { items: [], error: 'JSON 解析失败，请检查文件内容是否为合法 JSON' };
    }
  };
  const onPickFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const nextText = await file.text();
      setFileText(nextText);
      setFileName(file.name);
      setError('');
    } catch {
      setError('读取 JSON 文件失败');
    } finally {
      event.target.value = '';
    }
  };
  const clearFile = () => {
    setFileText('');
    setFileName('');
  };
  const onSubmit = async () => {
    const { items, error: parseError } = parseItems();
    if (items.length === 0) {
      setError(parseError || '内容为空或不是数组');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      setProgress(`准备导入 ${items.length} 题...`);
      const result = await onCreate(items, (completed, total) => {
        setProgress(`正在导入 ${completed}/${total} 题...`);
      });
      setProgress(`导入完成：成功写入 ${result.inserted} 题`);
      onCancel();
    } catch (e: any) {
      setError(e?.message || '导入失败');
      setProgress('');
    } finally {
      setSubmitting(false);
    }
  };
  const example = JSON.stringify([
    {
      "question_id": "hsk3_listening_001",
      "level": 3,
      "part": "listening",
      "question_text": "你明天要去哪里？",
      "options": [
        "去学校",
        "去商店",
        "去公园",
        "去银行"
      ],
      "correct_index": 0,
      "media": null,
      "explanation": "The question asks where you are going tomorrow, and the correct answer is '去学校' (to school).",
      "vocab_ids": [],
      "question_group": "hsk3_listening_group1",
      "group_seq": 1
    },
    {
      "question_id": "hsk3_listening_002",
      "level": 3,
      "part": "listening",
      "question_text": "这个苹果多少钱？",
      "options": [
        "五块钱",
        "十块钱",
        "十五块钱",
        "二十块钱"
      ],
      "correct_index": 1,
      "media": null,
      "explanation": "The question asks how much this apple costs, and the correct answer is '十块钱' (ten yuan).",
      "vocab_ids": [],
      "question_group": "hsk3_listening_group1",
      "group_seq": 2
    }
  ], null, 2);
  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center">
      <div className="bg-white rounded shadow p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-4">批量新增题目</h3>
        <div className="mb-3 text-sm text-gray-600">支持两种方式导入 JSON 数组：直接粘贴，或选择本地 `.json` 文件。可自动套用当前等级与类型。</div>
        <div className="flex items-center mb-2 space-x-3">
          <label className="inline-flex items-center text-sm text-gray-700">
            <span className="mr-2">题库版本</span>
            <select value={bankVersion} onChange={e => onBankVersionChange(e.target.value as QuestionBankVersion)} className="border rounded px-2 py-1">
              <option value="2.0">HSK2.0</option>
              <option value="3.0">HSK3.0</option>
            </select>
          </label>
          <label className="inline-flex items-center text-sm text-gray-700">
            <input type="checkbox" checked={useDefaults} onChange={e=>setUseDefaults(e.target.checked)} className="mr-2" />使用当前等级与类型作为默认值
          </label>
          <button onClick={() => { setText(example); clearFile(); }} className="px-2 py-1 border rounded text-sm">示例</button>
        </div>
        <div className="mb-2 text-xs text-gray-500">HSK2.0 写入 `HSK_question_bank`，HSK3.0 写入 `HSK3_question_bank`。</div>
        <div className="mb-3 rounded border border-dashed border-gray-300 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <label className="inline-flex cursor-pointer items-center px-3 py-2 border rounded text-sm hover:bg-gray-50">
              <Upload className="w-4 h-4 mr-2" />
              选择 JSON 文件
              <input type="file" accept=".json,application/json" className="hidden" onChange={onPickFile} />
            </label>
            {fileName && <span className="text-sm text-gray-600">当前文件：{fileName}</span>}
            {fileName && (
              <button type="button" onClick={clearFile} className="px-2 py-1 border rounded text-sm hover:bg-gray-50">
                清除文件
              </button>
            )}
          </div>
          <div className="mt-2 text-xs text-gray-500">
            选择文件后会优先使用文件内容导入，不会把大文件渲染到文本框里，避免页面卡顿。
          </div>
        </div>
        <textarea value={text} onChange={e => { setText(e.target.value); setError(''); if (fileName) clearFile(); }} rows={12} className="w-full border rounded px-3 py-2 font-mono text-sm" placeholder="也可以直接粘贴 JSON 数组到这里" />
        {progress && <div className="mt-2 text-sm text-blue-600">{progress}</div>}
        {error && <div className="mt-2 text-red-600 text-sm">{error}</div>}
        <div className="flex justify-end space-x-2 mt-4">
          <button onClick={onCancel} disabled={submitting} className="px-3 py-2 border rounded disabled:opacity-50">取消</button>
          <button onClick={onSubmit} disabled={submitting} className="px-3 py-2 bg-indigo-600 text-white rounded disabled:opacity-50">{submitting ? '导入中...' : '导入'}</button>
        </div>
      </div>
    </div>
  );
};

const ExportDialog: React.FC<{ onCancel: () => void; bankVersion: QuestionBankVersion; token: string | null }> = ({ onCancel, bankVersion, token }) => {
  const [selectedLevel, setSelectedLevel] = useState<number | null>(null);
  const [selectedParts, setSelectedParts] = useState<string[]>([]);
  const [availableParts, setAvailableParts] = useState<string[]>([]);
  const [loadingParts, setLoadingParts] = useState(false);
  const [preloadedQuestions, setPreloadedQuestions] = useState<any[] | null>(null);
  const [availableVocabPrefixes, setAvailableVocabPrefixes] = useState<string[]>([]);
  const [selectedVocabPrefixes, setSelectedVocabPrefixes] = useState<string[]>([]);
  const [preloading, setPreloading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState('');

  useEffect(() => {
    if (selectedLevel === null) { setAvailableParts([]); setSelectedParts([]); return; }
    setLoadingParts(true);
    localApi.listParts(selectedLevel, token || undefined, bankVersion)
      .then(res => {
        const merged = res.parts.map((p: any) => p.part).sort();
        setAvailableParts(merged);
        setSelectedParts(prev => prev.filter(p => merged.includes(p)));
      })
      .catch(() => {})
      .finally(() => setLoadingParts(false));
  }, [selectedLevel, bankVersion]);

  useEffect(() => {
    setPreloadedQuestions(null);
    setAvailableVocabPrefixes([]);
    setSelectedVocabPrefixes([]);
  }, [selectedLevel, selectedParts, bankVersion]);

  const getQidPrefix = (questionId: string) => questionId.split('_')[0];

  const togglePart = (part: string) => {
    setSelectedParts(prev =>
      prev.includes(part) ? prev.filter(p => p !== part) : [...prev, part]
    );
  };

  const toggleVocabPrefix = (prefix: string) => {
    setSelectedVocabPrefixes(prev =>
      prev.includes(prefix) ? prev.filter(p => p !== prefix) : [...prev, prefix]
    );
  };

  const fetchAllQuestions = async (): Promise<any[]> => {
    const allQuestions: any[] = [];
    const partsToFetch = selectedParts.length > 0 ? selectedParts : [undefined];
    for (const pt of partsToFetch) {
      setProgress(`正在加载 HSK ${selectedLevel}${pt ? ` · ${pt}` : ''}...`);
      let offset = 0;
      const limit = 500;
      let total = Infinity;
      while (offset < total) {
        const res = await localApi.browseQuestions({ level: selectedLevel ?? undefined, part: pt, limit, offset, bankVersion }, token || undefined);
        total = Number(res.total || 0);
        allQuestions.push(...res.questions);
        offset += res.questions.length;
        if (res.questions.length === 0) break;
      }
    }
    return allQuestions;
  };

  const onPreload = async () => {
    if (selectedLevel === null) { setError('请先选择等级'); return; }
    setPreloading(true);
    setError('');
    try {
      const questions = await fetchAllQuestions();
      setPreloadedQuestions(questions);
      const prefixes = Array.from(new Set(
        questions.map(q => getQidPrefix(q.question_id))
      )).filter(Boolean).sort();
      setAvailableVocabPrefixes(prefixes);
    } catch (e: any) {
      setError(e?.message || '加载失败');
    } finally {
      setPreloading(false);
      setProgress('');
    }
  };

  const onExport = async () => {
    if (selectedLevel === null) { setError('请选择等级'); return; }
    setExporting(true);
    setError('');
    try {
      const questions = preloadedQuestions ?? await fetchAllQuestions();
      const filtered = selectedVocabPrefixes.length > 0
        ? questions.filter(q => selectedVocabPrefixes.includes(getQidPrefix(q.question_id)))
        : questions;
      const exportData = filtered.map(q => ({
        question_id: q.question_id,
        level: q.level,
        part: q.part,
        question_text: q.question_text,
        options: q.options,
        correct_index: q.correct_index,
        media: q.media ?? null,
        explanation: q.explanation ?? null,
        vocab_ids: q.vocab_ids ?? [],
        question_group: q.question_group ?? null,
        group_seq: q.group_seq ?? null,
      }));
      const levelTag = selectedLevel;
      const partTag = selectedParts.length > 0 ? `_${selectedParts.join('-')}` : '';
      const vocabTag = selectedVocabPrefixes.length > 0 ? `_${selectedVocabPrefixes.join('-')}` : '';
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `hsk${bankVersion}_level${levelTag}${partTag}${vocabTag}_questions.json`;
      a.click();
      URL.revokeObjectURL(url);
      onCancel();
    } catch (e: any) {
      setError(e?.message || '导出失败');
    } finally {
      setExporting(false);
      setProgress('');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <div className="bg-white rounded shadow p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-4">批量导出题目</h3>
        <div className="mb-3 text-sm text-gray-600">选择要导出的 HSK 等级与题目类型，导出为 JSON 格式。</div>
        <div className="mb-1 text-xs text-gray-500">当前题库：{bankVersion === '3.0' ? 'HSK3.0 / HSK3_question_bank' : 'HSK2.0 / HSK_question_bank'}</div>
        <div className="mb-4 mt-3">
          <label className="block text-sm font-medium text-gray-700 mb-2">HSK 等级（单选）</label>
          <div className="flex flex-wrap gap-2">
            {[1, 2, 3, 4, 5, 6].map(lvl => (
              <label key={lvl} className={`inline-flex items-center gap-2 px-3 py-2 border rounded cursor-pointer transition ${selectedLevel === lvl ? 'border-orange-500 bg-orange-50' : 'hover:bg-gray-50'}`}>
                <input type="radio" name="export-level" checked={selectedLevel === lvl} onChange={() => setSelectedLevel(lvl)} className="accent-orange-500" />
                <span className="text-sm font-medium">HSK {lvl}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            题目类型（可多选，不选则导出全部）
            {loadingParts && <span className="ml-2 text-xs text-gray-400">加载中...</span>}
          </label>
          {availableParts.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {availableParts.map(pt => (
                <label key={pt} className={`inline-flex items-center gap-2 px-3 py-2 border rounded cursor-pointer transition ${selectedParts.includes(pt) ? 'border-indigo-500 bg-indigo-50' : 'hover:bg-gray-50'}`}>
                  <input type="checkbox" checked={selectedParts.includes(pt)} onChange={() => togglePart(pt)} className="accent-indigo-500" />
                  <span className="text-sm font-medium">{pt}</span>
                </label>
              ))}
            </div>
          ) : (
            <div className="text-sm text-gray-400">{selectedLevel === null ? '请先选择等级' : '暂无类型数据'}</div>
          )}
        </div>
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              题目分组（按 question_id 前缀，可多选，不选则导出全部）
            </label>
            <button
              onClick={onPreload}
              disabled={preloading || selectedLevel === null}
              className="px-2 py-1 text-xs border rounded hover:bg-gray-50 disabled:opacity-50"
            >
              {preloading ? (progress || '加载中...') : preloadedQuestions ? '重新加载' : '加载分组'}
            </button>
          </div>
          {preloadedQuestions === null ? (
            <div className="text-sm text-gray-400">
              {selectedLevel === null ? '请先选择等级' : '点击「加载分组」获取词汇前缀列表'}
            </div>
          ) : availableVocabPrefixes.length === 0 ? (
            <div className="text-sm text-gray-400">该范围内题目无词汇数据</div>
          ) : (
            <div className="flex flex-wrap gap-2 max-h-36 overflow-y-auto pr-1">
              {availableVocabPrefixes.map(prefix => (
                <label key={prefix} className={`inline-flex items-center gap-1.5 px-2 py-1.5 border rounded cursor-pointer transition text-xs ${selectedVocabPrefixes.includes(prefix) ? 'border-teal-500 bg-teal-50' : 'hover:bg-gray-50'}`}>
                  <input type="checkbox" checked={selectedVocabPrefixes.includes(prefix)} onChange={() => toggleVocabPrefix(prefix)} className="accent-teal-500" />
                  <span className="font-mono font-medium">{prefix}</span>
                </label>
              ))}
            </div>
          )}
          {preloadedQuestions !== null && (
            <div className="mt-1 text-xs text-gray-400">
              共 {preloadedQuestions.length} 题
              {selectedVocabPrefixes.length > 0 && (
                <span>，按前缀过滤后 {preloadedQuestions.filter(q => selectedVocabPrefixes.includes(q.question_id.split('_')[0])).length} 题</span>
              )}
            </div>
          )}
        </div>
        {progress && !preloading && <div className="text-sm text-blue-600 mb-3">{progress}</div>}
        {error && <div className="text-sm text-red-600 mb-3">{error}</div>}
        <div className="flex justify-end space-x-2 mt-4">
          <button onClick={onCancel} disabled={exporting || preloading} className="px-3 py-2 border rounded disabled:opacity-50">取消</button>
          <button onClick={onExport} disabled={exporting || preloading || selectedLevel === null} className="inline-flex items-center px-3 py-2 bg-orange-600 text-white rounded disabled:opacity-50 hover:bg-orange-700">
            <Download className="w-4 h-4 mr-2" />{exporting ? (progress || '导出中...') : '导出 JSON'}
          </button>
        </div>
      </div>
    </div>
  );
};

const UploadMediaDialog: React.FC<{ questionId: string; onCancel: ()=>void; onUploaded: (media:any)=>void }> = ({ questionId, onCancel, onUploaded }) => {
  const { token } = useAuthStore();
  const [imageFile, setImageFile] = useState<File|null>(null);
  const [audioFile, setAudioFile] = useState<File|null>(null);
  const [uploading, setUploading] = useState(false);
  const extFromType = (t: string) => t.includes('jpeg') ? 'jpg' : (t.split('/')[1] || 'bin');
  const buildKey = (kind: 'image'|'audio', file: File) => `l2/${questionId}/${kind}.${extFromType(file.type)}`;
  const onSubmit = async () => {
    setUploading(true);
    try {
      const media: any = {};
      if (imageFile) {
        const r = await localApi.uploadMedia(buildKey('image', imageFile), imageFile, token || undefined);
        media.image = r.url;
      }
      if (audioFile) {
        const r = await localApi.uploadMedia(buildKey('audio', audioFile), audioFile, token || undefined);
        media.audio = r.url;
      }
      await onUploaded(media);
    } catch (e:any) {
      alert(e?.message || '上传失败');
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center">
      <div className="bg-white rounded shadow p-6 w-full max-w-lg">
        <h3 className="text-lg font-semibold mb-4">上传媒体 {questionId}</h3>
        <label className="block text-sm font-medium text-gray-700 mb-1">图片</label>
        <input type="file" accept="image/*" onChange={e => setImageFile(e.target.files?.[0] || null)} className="w-full border rounded px-3 py-2 mb-3" />
        <label className="block text-sm font-medium text-gray-700 mb-1">音频</label>
        <input type="file" accept="audio/*" onChange={e => setAudioFile(e.target.files?.[0] || null)} className="w-full border rounded px-3 py-2 mb-4" />
        <div className="flex justify-end space-x-2">
          <button onClick={onCancel} className="px-3 py-2 border rounded">取消</button>
          <button onClick={onSubmit} disabled={uploading} className="px-3 py-2 bg-gray-800 text-white rounded">{uploading ? '上传中...' : '上传并保存'}</button>
        </div>
      </div>
    </div>
  );
}
